import React, { useEffect, useRef } from 'react';
import { useAlert } from 'react-alert';
import { useSelector } from 'react-redux';
import { Redirect } from 'react-router-dom';
import I18n from 'i18n-js';

import useSlotCards from '@lootboy/lib/slices/cardCollections/hooks/useSlotCards';

import Alert from '~/components/Alert';
import BottomSheetDialog, {
  TBottomSheetDialogElement,
} from '~/components/molecules/BottomSheetDialog';
import { useSnackbar } from '~/components/organisms/SnackbarContext';

// @ts-ignore lib
import { loggedSelector } from '~/redux/selectors';
import { LOGIN_REQUIRED_PATH } from '~/routes/paths';
import NoCardsDialogContent from '~/views/CollectionView/CardSelectionDialog/NoCardsDialogContent';
import RequiredCardDialogContent from '~/views/CollectionView/CardSelectionDialog/RequiredCardDialogContent';
import SlotCardDialogContent from '~/views/CollectionView/CardSelectionDialog/SlotCardDialogContent';

interface ICardSelectionDialogProps {
  onClose: () => void;
  collectionId: string;
  slotIndex: number;
}

const CLOSE_DIALOG_TIMEOUT = 250;

const CardSelectionDialog: React.FC<ICardSelectionDialogProps> = ({
  onClose,
  collectionId,
  slotIndex,
}) => {
  const alert = useAlert();
  const { dismissSnackbar } = useSnackbar();
  const isLoggedIn = useSelector(loggedSelector);
  const ref = useRef<TBottomSheetDialogElement>(null);
  const {
    cards,
    noCardsContent,
    dialogHeader,
    isSpecialSlot,
    selectCard,
    currentlySelectedCard,
    removeCurrentCard,
  } = useSlotCards(collectionId, slotIndex);

  useEffect(
    () => () => {
      dismissSnackbar();
    },
    [dismissSnackbar],
  );

  if (!isLoggedIn) {
    return <Redirect to={LOGIN_REQUIRED_PATH} />;
  }

  if (!cards || !noCardsContent || !dialogHeader) {
    return null;
  }

  const CardSelection = isSpecialSlot
    ? RequiredCardDialogContent
    : SlotCardDialogContent;

  const closeDialog = async () => {
    if (ref.current) {
      await ref.current.close();
    }
  };

  const handleActionError = (error: { message?: string }) => {
    alert.error(
      <Alert
        title={I18n.t('common_error')}
        body={error.message || ''}
      />,
    );
  };

  const onCardClick = async (cardId: string) => {
    if (cardId === currentlySelectedCard?._id) {
      removeCurrentCard().catch(handleActionError);
    } else {
      selectCard(cardId).catch(handleActionError);
    }
    setTimeout(() => {
      if (ref.current) {
        ref.current.close();
      }
    }, CLOSE_DIALOG_TIMEOUT);
  };

  return (
    <BottomSheetDialog
      ref={ref}
      onClose={onClose}
      beforeClose={dismissSnackbar}
      header={dialogHeader}
    >
      {cards.length || currentlySelectedCard ? (
        <CardSelection
          onCardClick={onCardClick}
          cards={cards}
          selectedCard={currentlySelectedCard}
        />
      ) : (
        <NoCardsDialogContent
          content={noCardsContent}
          closeDialog={closeDialog}
        />
      )}
    </BottomSheetDialog>
  );
};

export default CardSelectionDialog;
